import React, { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "chhq_chat_session";

function getOrCreateSessionId() {
  let id = localStorage.getItem(STORAGE_KEY);
  if (!id) {
    id =
      "s_" +
      Math.random().toString(36).slice(2, 10) +
      Date.now().toString(36).slice(-6);
    localStorage.setItem(STORAGE_KEY, id);
  }
  return id;
}

const SUGGESTED = [
  "What services do you offer?",
  "How much is credit repair?",
  "How do I book a consultation?",
  "Do you work with clients outside Indiana?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(getOrCreateSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateEmail, setEscalateEmail] = useState("");
  const [escalateName, setEscalateName] = useState("");
  const [escalateNotes, setEscalateNotes] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Load history on first open
  useEffect(() => {
    if (!open || messages.length > 0) return;
    fetch(`${BACKEND}/api/chat/history/${sessionId}`)
      .then((r) => r.json())
      .then((data) => {
        if (data?.messages?.length) setMessages(data.messages);
      })
      .catch(() => {});
  }, [open, sessionId, messages.length]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = (text ?? input).trim();
      if (!trimmed || streaming) return;
      setInput("");
      const userMsg = {
        role: "user",
        content: trimmed,
        created_at: new Date().toISOString(),
      };
      const assistantPlaceholder = {
        role: "assistant",
        content: "",
        created_at: new Date().toISOString(),
        _streaming: true,
      };
      setMessages((m) => [...m, userMsg, assistantPlaceholder]);
      setStreaming(true);

      try {
        const res = await fetch(`${BACKEND}/api/chat/send`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, message: trimmed }),
        });

        if (!res.ok || !res.body) {
          throw new Error(`HTTP ${res.status}`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          // Parse SSE chunks delimited by \n\n
          const chunks = buffer.split("\n\n");
          buffer = chunks.pop() || "";
          for (const chunk of chunks) {
            if (!chunk.startsWith("data: ")) continue;
            const payload = chunk.slice(6);
            if (payload === "[DONE]") continue;
            if (payload.startsWith("[error:")) {
              toast.error("Chat error. Please try again.");
              continue;
            }
            setMessages((m) => {
              const next = [...m];
              const last = next[next.length - 1];
              if (last && last._streaming) {
                next[next.length - 1] = {
                  ...last,
                  content: last.content + payload,
                };
              }
              return next;
            });
          }
        }
      } catch (e) {
        toast.error("Couldn't reach the chat. Please try again.");
      } finally {
        setStreaming(false);
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last._streaming) {
            next[next.length - 1] = { ...last, _streaming: false };
          }
          return next;
        });
      }
    },
    [input, streaming, sessionId]
  );

  const handleEscalate = async () => {
    if (!escalateEmail) {
      toast.error("Please enter your email.");
      return;
    }
    try {
      const res = await fetch(`${BACKEND}/api/chat/escalate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          visitor_email: escalateEmail,
          visitor_name: escalateName || undefined,
          notes: escalateNotes || undefined,
        }),
      });
      if (!res.ok) throw new Error("escalate failed");
      toast.success("Devyne will be in touch within one business day!");
      setEscalateOpen(false);
      setEscalateEmail("");
      setEscalateName("");
      setEscalateNotes("");
      // Add an inline confirmation in the chat
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: `Got it — Devyne will reach out to ${escalateEmail} within one business day. In the meantime, anything else I can help with?`,
          created_at: new Date().toISOString(),
        },
      ]);
    } catch {
      toast.error("Couldn't submit. Please try again or email info@thechhq.com.");
    }
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          data-testid="chat-widget-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-4 sm:bottom-20 sm:right-6 z-50 bg-gold text-navy rounded-full shadow-xl hover:shadow-2xl transition-all px-5 py-3.5 flex items-center gap-2 font-medium text-sm hover:scale-105"
          aria-label="Open chat"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="hidden sm:inline">Ask Us Anything</span>
          <span className="sm:hidden">Chat</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 right-4 left-4 sm:left-auto sm:bottom-20 sm:right-6 sm:w-[400px] z-50 bg-soft rounded-2xl shadow-2xl border border-navy/10 flex flex-col overflow-hidden"
          style={{ maxHeight: "calc(100vh - 2rem)", height: "600px" }}
        >
          {/* Header */}
          <div className="bg-navy text-soft px-5 py-4 flex items-center justify-between">
            <div>
              <div className="font-serif text-base font-medium">Chat with ConnectHER</div>
              <div className="text-[11px] text-soft/60 mt-0.5 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                AI assistant · powered by Claude
              </div>
            </div>
            <button
              data-testid="chat-widget-close-btn"
              onClick={() => setOpen(false)}
              className="p-1.5 hover:bg-soft/10 rounded-lg transition-colors"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chat-widget-messages"
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-soft"
          >
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="bg-white border border-navy/5 rounded-2xl rounded-tl-md px-4 py-3 text-sm text-navy/80 max-w-[85%]">
                  Hey there! 👋 I&apos;m Devyne&apos;s AI assistant. I can answer questions about our services,
                  pricing, hours, or help you book a consultation. What brings you in?
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTED.map((q) => (
                    <button
                      key={q}
                      data-testid={`chat-suggested-${q.slice(0, 10)}`}
                      onClick={() => sendMessage(q)}
                      className="text-xs bg-white border border-gold/30 text-navy px-3 py-1.5 rounded-full hover:bg-gold/10 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-navy text-soft rounded-2xl rounded-tr-md"
                      : "bg-white border border-navy/5 text-navy rounded-2xl rounded-tl-md"
                  }`}
                >
                  {m.content}
                  {m._streaming && (
                    <span className="inline-block w-1.5 h-3.5 bg-navy/40 ml-0.5 animate-pulse align-middle"></span>
                  )}
                </div>
              </div>
            ))}

            {streaming && messages[messages.length - 1]?.role === "user" && (
              <div className="flex justify-start">
                <div className="bg-white border border-navy/5 rounded-2xl rounded-tl-md px-4 py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-navy/40" />
                </div>
              </div>
            )}
          </div>

          {/* Escalation drawer */}
          {escalateOpen && (
            <div data-testid="chat-escalate-form" className="bg-white border-t border-navy/10 p-4 space-y-2.5">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium text-navy">Connect with Devyne</div>
                <button onClick={() => setEscalateOpen(false)} className="text-navy/40 hover:text-navy">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <Input
                data-testid="chat-escalate-email"
                type="email"
                placeholder="Your email *"
                value={escalateEmail}
                onChange={(e) => setEscalateEmail(e.target.value)}
                className="h-9 text-sm"
              />
              <Input
                data-testid="chat-escalate-name"
                placeholder="Your name (optional)"
                value={escalateName}
                onChange={(e) => setEscalateName(e.target.value)}
                className="h-9 text-sm"
              />
              <Textarea
                data-testid="chat-escalate-notes"
                placeholder="What would you like Devyne to know? (optional)"
                value={escalateNotes}
                onChange={(e) => setEscalateNotes(e.target.value)}
                rows={2}
                className="text-sm resize-none"
              />
              <Button
                data-testid="chat-escalate-submit"
                onClick={handleEscalate}
                className="w-full h-9 bg-gold hover:bg-gold/90 text-navy text-sm font-medium"
              >
                Send to Devyne
              </Button>
            </div>
          )}

          {/* Input bar */}
          <div className="border-t border-navy/10 bg-white p-3">
            <div className="flex items-end gap-2">
              <Textarea
                data-testid="chat-widget-input"
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Type your question..."
                rows={1}
                disabled={streaming}
                className="flex-1 resize-none min-h-[40px] max-h-[120px] text-sm border-navy/10 focus:border-gold"
              />
              <Button
                data-testid="chat-widget-send-btn"
                onClick={() => sendMessage()}
                disabled={streaming || !input.trim()}
                size="icon"
                className="bg-navy hover:bg-navy/90 text-soft h-10 w-10 shrink-0"
              >
                {streaming ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <button
              data-testid="chat-widget-escalate-btn"
              onClick={() => setEscalateOpen(true)}
              className="text-[11px] text-navy/50 hover:text-navy mt-2 flex items-center gap-1 transition-colors"
            >
              <Mail className="w-3 h-3" />
              Prefer to talk to Devyne directly? Click here
            </button>
          </div>
        </div>
      )}
    </>
  );
}
